import React, { useState, useEffect } from "react";
import { Button } from "../components/common/Button";
import { adjustCrossMidnightDate } from '../utils/dateTimeUtils';

interface DateTimeSelectorProps {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  bookingType: 'Daily' | 'Monthly';
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onApiDatesChange?: (apiDates: { startDate: string; endDate: string; startTime: string; endTime: string }) => void;
}

// Helper function to get rounded up hour
const getRoundedUpTime = () => {
  const now = new Date();
  const currentMinutes = now.getMinutes();
  
  if (currentMinutes > 0) {
    now.setHours(now.getHours() + 1);
  }
  now.setMinutes(0);
  now.setSeconds(0);
  
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = '00';
  return `${hours}:${minutes}`;
};

// Helper function to add hours to a time string and get the resulting date/time
const addHoursToTime = (dateStr: string, timeStr: string, hoursToAdd: number) => {
  const date = new Date(dateStr);
  const [hours, minutes] = timeStr.split(':').map(Number);
  date.setHours(hours, minutes, 0, 0);
  date.setHours(date.getHours() + hoursToAdd);
  return date;
};

export function DateTimeSelector({
  startDate,
  endDate,
  startTime,
  endTime,
  bookingType,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onApiDatesChange
}: DateTimeSelectorProps) {
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize times on mount if not provided
  useEffect(() => {
    if (!startTime && !isInitialized) {
      const roundedStartTime = getRoundedUpTime();
      
      // Use setTimeout to batch all updates in next tick
      setTimeout(() => {
        onStartTimeChange(roundedStartTime);
        
        // Always auto-calculate end date/time for Daily bookings during initialization
        if (bookingType === 'Daily') {
          const currentStartDate = startDate || new Date().toISOString().split('T')[0];
          const endDateTime = addHoursToTime(currentStartDate, roundedStartTime, 24);
          const endHours = endDateTime.getHours().toString().padStart(2, '0');
          const endMinutes = endDateTime.getMinutes().toString().padStart(2, '0');
          
          onEndDateChange(endDateTime.toISOString().split('T')[0]);
          onEndTimeChange(`${endHours}:${endMinutes}`);
        }
        
        setIsInitialized(true);
      }, 0);
    }
  }, [startDate, startTime, bookingType, onStartTimeChange, onEndDateChange, onEndTimeChange, isInitialized]);

  useEffect(() => {
    if (startDate && endDate && startTime && endTime) {
      const start = new Date(startDate);
      const [startHour, startMinute] = startTime.split(":").map(Number);
      start.setHours(startHour, startMinute, 0, 0);

      const end = new Date(endDate);
      const [endHour, endMinute] = endTime.split(":").map(Number);
      end.setHours(endHour, endMinute, 0, 0);

      const diffInMs = end.getTime() - start.getTime();
      const diffInHours = diffInMs / (1000 * 60 * 60);

      // Calculate API-ready dates (handle cross-midnight scenarios)
      const adjustedParams = adjustCrossMidnightDate({
        startDate,
        endDate,
        startTime,
        endTime
      });

      // Notify parent with API-ready dates
      if (onApiDatesChange) {
        onApiDatesChange(adjustedParams);
      }

      if (diffInHours < 0) {
        setError("End date/time must be after start date/time");
        setSuccess("");
      } else if (bookingType === 'Daily' && diffInHours < 24) {
        // Auto-adjust to 24 hours minimum
        const adjustedEndDateTime = addHoursToTime(startDate, startTime, 24);
        
        setTimeout(() => {
          onEndDateChange(adjustedEndDateTime.toISOString().split('T')[0]);
          const endHours = adjustedEndDateTime.getHours().toString().padStart(2, '0');
          const endMinutes = adjustedEndDateTime.getMinutes().toString().padStart(2, '0');
          onEndTimeChange(`${endHours}:${endMinutes}`);
        }, 0);
        
        setError("");
        setSuccess(`Duration adjusted to 24 hours (1 day)`);
      } else if (bookingType === 'Monthly') {
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
        const startMonth = start.getMonth();
        const isFebruary = startMonth === 1;
        
        // For February, minimum is 28 days (or 29 in leap year)
        // For all other months, minimum is 30 days
        const minDays = isFebruary ? 28 : 30;
        
        if (diffInDays < minDays) {
          setError(`Monthly booking requires minimum ${minDays} days`);
          setSuccess("");
        } else {
          setError("");
          const days = Math.floor(diffInHours / 24);
          const hours = Math.floor(diffInHours % 24);
          const minutes = Math.round((diffInHours % 1) * 60);
          
          let durationText = "Duration: ";
          if (days > 0) durationText += `${days} day${days > 1 ? 's' : ''} `;
          if (hours > 0) durationText += `${hours} hour${hours > 1 ? 's' : ''} `;
          if (minutes > 0) durationText += `${minutes} minute${minutes > 1 ? 's' : ''}`;
          
          setSuccess(durationText.trim());
        }
      } else {
        setError("");
        const days = Math.floor(diffInHours / 24);
        const hours = Math.floor(diffInHours % 24);
        const minutes = Math.round((diffInHours % 1) * 60);
        
        let durationText = "Duration: ";
        if (days > 0) durationText += `${days} day${days > 1 ? 's' : ''} `;
        if (hours > 0) durationText += `${hours} hour${hours > 1 ? 's' : ''} `;
        if (minutes > 0) durationText += `${minutes} minute${minutes > 1 ? 's' : ''}`;
        
        setSuccess(durationText.trim());
      }
    }
  }, [startDate, endDate, startTime, endTime, bookingType, onApiDatesChange]);

  // Handle booking type changes - auto-adjust end date/time when switching to Daily
  useEffect(() => {
    if (bookingType === 'Daily' && startDate && startTime) {
      const endDateTime = addHoursToTime(startDate, startTime, 24);
      const endHours = endDateTime.getHours().toString().padStart(2, '0');
      const endMinutes = endDateTime.getMinutes().toString().padStart(2, '0');
      
      onEndDateChange(endDateTime.toISOString().split('T')[0]);
      onEndTimeChange(`${endHours}:${endMinutes}`);
    }
  }, [bookingType, startDate, startTime, onEndDateChange, onEndTimeChange]);

  // Update end time when start time changes
  const handleStartTimeChange = (newStartTime: string) => {
    onStartTimeChange(newStartTime);
    
    // Only auto-calculate end date/time for Daily bookings
    if (bookingType === 'Daily' && startDate && newStartTime) {
      const endDateTime = addHoursToTime(startDate, newStartTime, 24);
      onEndDateChange(endDateTime.toISOString().split('T')[0]);
      
      const endHours = endDateTime.getHours().toString().padStart(2, '0');
      const endMinutes = endDateTime.getMinutes().toString().padStart(2, '0');
      onEndTimeChange(`${endHours}:${endMinutes}`);
    }
  };

  // Update end time when start date changes
  const handleStartDateChange = (newStartDate: string) => {
    onStartDateChange(newStartDate);
    
    // Only auto-calculate end date/time for Daily bookings
    if (bookingType === 'Daily' && startTime) {
      const endDateTime = addHoursToTime(newStartDate, startTime, 24);
      onEndDateChange(endDateTime.toISOString().split('T')[0]);
      
      const endHours = endDateTime.getHours().toString().padStart(2, '0');
      const endMinutes = endDateTime.getMinutes().toString().padStart(2, '0');
      onEndTimeChange(`${endHours}:${endMinutes}`);
    }
  };

  const handleSubmit = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    if (error) {
      return;
    }

    console.log({
      startDate,
      endDate,
      startTime,
      endTime,
    });

    alert("Date and time range selected successfully!");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Start Date */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Start Date</label>
          <div className="max-w-40">
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Start Time */}
        <div className="space-y-2">
          <label htmlFor="start-time" className="block text-sm font-medium text-gray-700">Start Time</label>
          <div className="max-w-32">
            <input
              id="start-time"
              type="time"
              value={startTime}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">End Date</label>
          <div className="max-w-40">
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              min={startDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* End Time */}
        <div className="space-y-2">
          <label htmlFor="end-time" className="block text-sm font-medium text-gray-700">End Time</label>
          <div className="max-w-32">
            <input
              id="end-time"
              type="time"
              value={endTime}
              onChange={(e) => onEndTimeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      {/* Minimum Duration Info */}
      {bookingType === 'Daily' && (
        <div className="text-xs text-gray-500">
          Minimum duration: 24 hours
        </div>
      )}

      {/* Validation Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && !error && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700">
          ✓ {success}
        </div>
      )}
    </div>
  );
}